import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import SweetAlert from "react-bootstrap-sweetalert"
import {
  Badge,
  Button,
  Col,
  Collapse,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from "react-bootstrap"
import { toast } from "react-toastify"
import { roleCheck } from "utils/index"
import {
  ACCOUNTS,
  OPERATIONS,
  MAX_SIZE_UPLOADED_FILE,
  MAX_SIZE_UPLOADED_FILE_TEXT
} from "utils/constants"
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../reducers"
import {
  approveReimbursementLineItem,
  declineReimbursementLineItem,
  getReimbursementLineItemAuditsDashboard,
  getReimbursementLineItemsDashboard,
  openDashboardSubmitterReimbursementPdf,
  setReimbursementLineItemPaid,
  updateReimbursementLineItem
} from "requesters/reimbursementLineItemsRequester"
import { formatReimbursementTotal, sumReimbursementAmounts } from "utils/reimbursementTotals"

const SYSTEM_ADMIN_ROLES = ["admin", "super_admin"]

function normalizeReimbursementStatus(raw) {
  if (raw === "submitted") return "pending"
  return raw || "pending"
}

function parseDashboardRows(payload) {
  if (!payload?.data) return []
  const list = Array.isArray(payload.data) ? payload.data : [payload.data]
  return list.map(row => {
    const a = row.attributes || {}
    return {
      id: row.id,
      submitterId: a.submitter_id ?? a.submitterId,
      occurredOn: a.occurred_on || a.occurredOn,
      description: a.description,
      amount: a.amount,
      receiptUrl: a.receipt_url || a.receiptUrl,
      submitterName: a.submitter_name || a.submitterName,
      submitterEmail: a.submitter_email || a.submitterEmail,
      createdAt: a.created_at || a.createdAt,
      status: normalizeReimbursementStatus(a.status),
      declinedReason: a.declined_reason || a.declinedReason,
      reviewedByName: a.reviewed_by_name || a.reviewedByName,
      reviewedAt: a.reviewed_at || a.reviewedAt,
      paid: Boolean(a.paid),
      paidAt: a.paid_at || a.paidAt
    }
  })
}

const STATUS_SECTION_ORDER = ["pending", "approved", "declined", "closed"]

function statusSectionSortIndex(status) {
  const i = STATUS_SECTION_ORDER.indexOf(status)
  return i === -1 ? STATUS_SECTION_ORDER.length : i
}

function groupKeyForRow(row) {
  if (row.submitterId != null) return `id:${row.submitterId}`
  if (row.submitterEmail) return `email:${row.submitterEmail}`
  return `row:${row.id}`
}

function groupDashboardRowsByStatus(rows) {
  const buckets = new Map()
  for (const row of rows) {
    const status = row.status || "pending"
    if (!buckets.has(status)) buckets.set(status, [])
    buckets.get(status).push(row)
  }
  const sections = []
  for (const status of STATUS_SECTION_ORDER) {
    const list = buckets.get(status)
    if (list?.length) sections.push({ status, rows: list })
  }
  const rest = [...buckets.keys()]
    .filter(s => !STATUS_SECTION_ORDER.includes(s))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
  for (const status of rest) {
    const list = buckets.get(status)
    if (list?.length) sections.push({ status, rows: list })
  }
  return sections
}

function formatStatusSectionTitle(status) {
  if (!status) return "Unknown"
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function closedRowPaidAtPartitionKey(row) {
  const raw = row.paidAt != null ? String(row.paidAt).trim() : ""
  if (!raw) return `__missing_paid_at__|id:${row.id}`
  const ms = Date.parse(raw)
  if (!Number.isFinite(ms)) return `__missing_paid_at__|id:${row.id}`
  const d = new Date(ms)
  d.setUTCSeconds(0, 0)
  return d.toISOString()
}

function maxPaidAtMsForRows(rows) {
  return rows.reduce((max, r) => {
    const t = r.paidAt ? Date.parse(String(r.paidAt)) : NaN
    const v = Number.isFinite(t) ? t : 0
    return Math.max(max, v)
  }, 0)
}

function metricsForSubmitterSlice(groupKey, sorted) {
  const dates = sorted.map(r => r.occurredOn).filter(Boolean)
  const minDate = dates[0] || "—"
  const maxDate = dates[dates.length - 1] || "—"
  const approvedOrClosed = sorted.filter(r => r.status === "approved" || r.status === "closed")
  const unpaidApproved = approvedOrClosed.filter(r => !r.paid)
  const paidApproved = approvedOrClosed.filter(r => r.paid)
  const allPaid = approvedOrClosed.length > 0 && unpaidApproved.length === 0
  const hasMixedPaidState = paidApproved.length > 0 && unpaidApproved.length > 0
  const head = sorted[0]
  const statusSet = [...new Set(sorted.map(r => r.status))]
  return {
    groupKey,
    submitterId: head.submitterId,
    submitterName: head.submitterName,
    submitterEmail: head.submitterEmail,
    items: sorted,
    minDate,
    maxDate,
    dateLabel: minDate === maxDate ? minDate : `${minDate} – ${maxDate}`,
    itemCount: sorted.length,
    statusLabels: statusSet,
    allApproved: sorted.length > 0 && sorted.every(r => r.status === "approved" || r.status === "closed"),
    unpaidApprovedIds: unpaidApproved.map(r => r.id),
    paidApprovedIds: paidApproved.map(r => r.id),
    allPaid,
    hasMixedPaidState,
    approvedCount: approvedOrClosed.length
  }
}

function aggregateReimbursementsBySubmitter(rows) {
  const submitterMap = new Map()
  for (const row of rows) {
    const key = groupKeyForRow(row)
    if (!submitterMap.has(key)) submitterMap.set(key, [])
    submitterMap.get(key).push(row)
  }

  const groups = []
  for (const [baseKey, items] of submitterMap) {
    const byStatus = new Map()
    for (const row of items) {
      const st = row.status || "pending"
      if (!byStatus.has(st)) byStatus.set(st, [])
      byStatus.get(st).push(row)
    }
    const statuses = [...byStatus.keys()].sort((a, b) => statusSectionSortIndex(a) - statusSectionSortIndex(b))
    for (const st of statuses) {
      const slice = byStatus.get(st)
      if (st === "closed") {
        const byPaidAt = new Map()
        for (const row of slice) {
          const pk = closedRowPaidAtPartitionKey(row)
          if (!byPaidAt.has(pk)) byPaidAt.set(pk, [])
          byPaidAt.get(pk).push(row)
        }
        const partitionKeys = [...byPaidAt.keys()].sort((a, b) => {
          if (a.startsWith("__missing_paid_at__")) return 1
          if (b.startsWith("__missing_paid_at__")) return -1
          return b.localeCompare(a, undefined, { sensitivity: "base" })
        })
        for (const pk of partitionKeys) {
          const partRows = byPaidAt.get(pk)
          const sorted = [...partRows].sort((a, b) => (a.occurredOn || "").localeCompare(b.occurredOn || ""))
          const paidKey = pk.startsWith("__missing_paid_at__") ? pk : encodeURIComponent(pk)
          groups.push(metricsForSubmitterSlice(`${baseKey}|status:closed|paid_at:${paidKey}`, sorted))
        }
      } else {
        const sorted = [...slice].sort((a, b) => (a.occurredOn || "").localeCompare(b.occurredOn || ""))
        groups.push(metricsForSubmitterSlice(`${baseKey}|status:${st}`, sorted))
      }
    }
  }

  return groups.sort((a, b) => {
    const nameCmp = (a.submitterName || "").localeCompare(b.submitterName || "", undefined, { sensitivity: "base" })
    if (nameCmp !== 0) return nameCmp
    const sa = a.statusLabels[0] || ""
    const sb = b.statusLabels[0] || ""
    const statusCmp = statusSectionSortIndex(sa) - statusSectionSortIndex(sb)
    if (statusCmp !== 0) return statusCmp
    if (sa === "closed" && sb === "closed") {
      return maxPaidAtMsForRows(b.items) - maxPaidAtMsForRows(a.items)
    }
    return 0
  })
}

function parseAuditRows(payload) {
  if (!payload?.data) return []
  const list = Array.isArray(payload.data) ? payload.data : [payload.data]
  return list.map(row => {
    const a = row.attributes || {}
    return {
      id: row.id,
      reimbursementLineItemId: a.reimbursement_line_item_id || a.reimbursementLineItemId,
      event: a.event,
      comment: a.comment,
      changedByName: a.changed_by_name || a.changedByName,
      changedAt: a.changed_at || a.changedAt,
      changedFields: a.changed_fields || a.changedFields || [],
      changeSummary: a.change_summary || a.changeSummary || ""
    }
  })
}

function canAccessDashboard(systemRole, currentRole) {
  return roleCheck(systemRole, currentRole, OPERATIONS) || roleCheck(systemRole, currentRole, ACCOUNTS)
}

function isAccountsTeam(currentRole) {
  if (Array.isArray(currentRole)) return currentRole.some(role => role.includes(ACCOUNTS))
  return typeof currentRole === "string" ? currentRole.includes(ACCOUNTS) : false
}

function isSystemAdmin(systemRole) {
  return SYSTEM_ADMIN_ROLES.includes(systemRole)
}

function statusBadgeVariant(status) {
  if (status === "approved") return "success"
  if (status === "closed") return "info"
  if (status === "declined") return "danger"
  return "warning"
}

function ConsolidatedAccountsStatusBadge({ group }) {
  if (group.statusLabels.length === 1) {
    const s = group.statusLabels[0]
    return (
      <Badge variant={statusBadgeVariant(s)}>{s}</Badge>
    )
  }
  return (
    <Badge variant="secondary" title={`${group.itemCount} line items`}>
      Mixed statuses ({group.itemCount})
    </Badge>
  )
}

function ConsolidatedPaidCell({ group, canMarkPaid, markingGroupKey, onRequestMarkAll }) {
  if (!canMarkPaid || group.approvedCount === 0) return "—"

  const busy = markingGroupKey === group.groupKey
  const payButtonTooltipId = `reimb-paid-${encodeURIComponent(group.groupKey).replace(/%/g, "")}`
  const unpaidN = group.unpaidApprovedIds.length

  const markPaidTooltip = group.hasMixedPaidState
    ? `Partially paid — ${unpaidN} unpaid of ${group.approvedCount} approved line item${group.approvedCount === 1 ? "" : "s"}`
    : `Unpaid · ${group.approvedCount} line item${group.approvedCount === 1 ? "" : "s"}`

  if (group.allPaid) {
    return (
      <div className="d-flex flex-row flex-wrap align-items-center" style={{ gap: "0.35rem" }}>
        <Badge variant="success">Paid</Badge>
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => onRequestMarkAll(group, false)}>
          Mark all unpaid
        </Button>
      </div>
    )
  }

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 100 }}
      overlay={<Tooltip id={payButtonTooltipId}>{markPaidTooltip}</Tooltip>}>
      <span className="d-inline-block">
        <Button
          size="sm"
          variant="outline-success"
          disabled={busy}
          onClick={() => onRequestMarkAll(group, true)}>
          Mark {unpaidN} unpaid as paid
        </Button>
      </span>
    </OverlayTrigger>
  )
}


export default function ReimbursementsDashboard() {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser?.attributes?.role

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editRowId, setEditRowId] = useState(null)
  const [editForm, setEditForm] = useState({
    occurredOn: "",
    description: "",
    amount: "",
    receiptFile: null
  })
  const [saving, setSaving] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [declineRowId, setDeclineRowId] = useState(null)
  const [declineReason, setDeclineReason] = useState("")
  const [declining, setDeclining] = useState(false)
  const [approvingId, setApprovingId] = useState(null)
  const [markingPaidRowId, setMarkingPaidRowId] = useState(null)
  const [markingPaidGroupKey, setMarkingPaidGroupKey] = useState(null)
  const [pdfDetailsLoadingGroupKey, setPdfDetailsLoadingGroupKey] = useState(null)
  const [submitterOptions, setSubmitterOptions] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [auditRows, setAuditRows] = useState([])
  const [paidConfirm, setPaidConfirm] = useState({ row: null, group: null, paid: null })

  const [filterUserId, setFilterUserId] = useState("")
  const [filterDateFrom, setFilterDateFrom] = useState("")
  const [filterDateTo, setFilterDateTo] = useState("")
  const [filterPaid, setFilterPaid] = useState("unpaid")
  const [filterStatus, setFilterStatus] = useState(() => (isSystemAdmin(systemRole) ? "pending" : "all"))
  const [filtersOpen, setFiltersOpen] = useState(true)

  const loadRows = useCallback(() => {
    if (!baseUrl) return Promise.resolve()
    setLoading(true)
    const queryParams = {}
    if (filterUserId) queryParams.user_id = Number(filterUserId)
    if (filterDateFrom) queryParams.occurred_on_from = filterDateFrom
    if (filterDateTo) queryParams.occurred_on_to = filterDateTo
    if (filterPaid && filterPaid !== "all") queryParams.paid_filter = filterPaid

    const accountsOnlyView =
      roleCheck(systemRole, currentRole, ACCOUNTS) && !isSystemAdmin(systemRole)
    if (filterStatus && filterStatus !== "all" && !accountsOnlyView) {
      queryParams.status_filter = filterStatus
    }

    return getReimbursementLineItemsDashboard(baseUrl, queryParams)
      .then(res => {
        if (res.status === 200 && res.response) {
          setRows(parseDashboardRows(res.response))
          const submitters = res.response.meta?.submitters
          if (Array.isArray(submitters)) {
            setSubmitterOptions(submitters)
          }
        } else if (res.error) {
          toast.error("Could not load reimbursements.")
        }
      })
      .finally(() => setLoading(false))
  }, [baseUrl, filterUserId, filterDateFrom, filterDateTo, filterPaid, filterStatus, systemRole, currentRole])

  const loadAuditRows = useCallback(() => {
    if (!baseUrl) return Promise.resolve()
    setHistoryLoading(true)
    return getReimbursementLineItemAuditsDashboard(baseUrl)
      .then(res => {
        if (res.status === 200 && res.response) {
          setAuditRows(parseAuditRows(res.response))
        } else if (res.error) {
          toast.error("Could not load reimbursement history.")
        }
      })
      .finally(() => setHistoryLoading(false))
  }, [baseUrl])

  useEffect(() => {
    if (!canAccessDashboard(systemRole, currentRole)) {
      window.location = "/"
      return
    }
    loadRows()
  }, [loadRows, systemRole, currentRole])

  const openEdit = row => {
    if (row.status !== "pending") {
      toast.error("Only pending items can be edited.")
      return
    }
    setEditRowId(row.id)
    setEditForm({
      occurredOn: row.occurredOn || "",
      description: row.description || "",
      amount: row.amount != null ? String(row.amount) : "",
      receiptFile: null
    })
    setShowEditModal(true)
  }

  const closeEdit = () => {
    setShowEditModal(false)
    setEditRowId(null)
    setEditForm({
      occurredOn: "",
      description: "",
      amount: "",
      receiptFile: null
    })
  }

  const handleSaveEdit = async e => {
    e.preventDefault()
    if (!editRowId) return

    if (!editForm.occurredOn || !editForm.description.trim() || editForm.amount === "") {
      toast.error("Date, description, and amount are required.")
      return
    }
    const n = parseFloat(editForm.amount)
    if (Number.isNaN(n) || n <= 0) {
      toast.error("Amount must be a positive number.")
      return
    }
    if (editForm.receiptFile && editForm.receiptFile.size > MAX_SIZE_UPLOADED_FILE) {
      toast.error(`Receipt must be under ${MAX_SIZE_UPLOADED_FILE_TEXT}.`)
      return
    }

    setSaving(true)
    const fd = new FormData()
    fd.append("occurred_on", editForm.occurredOn)
    fd.append("description", editForm.description.trim())
    fd.append("amount", String(n))
    if (editForm.receiptFile) {
      fd.append("receipt", editForm.receiptFile)
    }

    const res = await updateReimbursementLineItem(baseUrl, editRowId, fd)
    setSaving(false)

    if (res.error) {
      const errs = res.error.response?.data?.errors
      const msg = Array.isArray(errs)
        ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
        : (res.error.message || "Could not save.")
      toast.error(msg || "Could not save.")
      return
    }
    if (res.status >= 200 && res.status < 300) {
      toast.success("Reimbursement updated.")
      closeEdit()
      loadRows()
      if (showHistory) loadAuditRows()
    }
  }

  const openDecline = row => {
    setDeclineRowId(row.id)
    setDeclineReason("")
    setShowDeclineModal(true)
  }

  const closeDecline = () => {
    setShowDeclineModal(false)
    setDeclineRowId(null)
    setDeclineReason("")
  }

  const submitDecline = async e => {
    e.preventDefault()
    if (!declineRowId) return
    const trimmed = declineReason.trim()
    if (!trimmed) {
      toast.error("Please enter a reason for declining.")
      return
    }
    setDeclining(true)
    const res = await declineReimbursementLineItem(baseUrl, declineRowId, trimmed)
    setDeclining(false)
    if (res.error) {
      const errs = res.error.response?.data?.errors
      const msg = Array.isArray(errs)
        ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
        : (res.error.message || "Could not decline.")
      toast.error(msg || "Could not decline.")
      return
    }
    toast.success("Reimbursement declined.")
    closeDecline()
    loadRows()
    if (showHistory) loadAuditRows()
  }

  const handleApprove = async row => {
    setApprovingId(row.id)
    const res = await approveReimbursementLineItem(baseUrl, row.id)
    setApprovingId(null)
    if (res.error) {
      const errs = res.error.response?.data?.errors
      const msg = Array.isArray(errs)
        ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
        : (res.error.message || "Could not approve.")
      toast.error(msg || "Could not approve.")
      return
    }
    toast.success("Reimbursement approved.")
    loadRows()
    if (showHistory) loadAuditRows()
  }

  const handleTogglePaid = async (row, paid) => {
    setMarkingPaidRowId(row.id)
    try {
      const res = await setReimbursementLineItemPaid(baseUrl, row.id, paid)
      if (res.error) {
        const errs = res.error.response?.data?.errors
        const msg = Array.isArray(errs)
          ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
          : (res.error.message || "Could not update paid status.")
        toast.error(msg || "Could not update paid status.")
        return
      }
      toast.success(paid ? "Marked as paid." : "Marked as unpaid.")
      loadRows()
      if (showHistory) loadAuditRows()
    } finally {
      setMarkingPaidRowId(null)
    }
  }

  const handleToggleGroupPaid = async (group, paid) => {
    const ids = paid ? group.unpaidApprovedIds : group.paidApprovedIds
    if (!ids.length) return
    setMarkingPaidGroupKey(group.groupKey)
    try {
      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i]
        const res = await setReimbursementLineItemPaid(baseUrl, id, paid)
        if (res.error) {
          const errs = res.error.response?.data?.errors
          const msg = Array.isArray(errs)
            ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
            : (res.error.message || "Could not update paid status.")
          toast.error(msg || "Could not update paid status.")
          return
        }
      }
      toast.success(paid ? "Marked reimbursements as paid." : "Marked reimbursements as unpaid.")
      loadRows()
      if (showHistory) loadAuditRows()
    } finally {
      setMarkingPaidGroupKey(null)
    }
  }

  const handlePaidCheckboxChange = (row, checked) => {
    setPaidConfirm({ row, group: null, paid: checked })
  }

  const confirmTogglePaid = async () => {
    try {
      if (paidConfirm.group != null && typeof paidConfirm.paid === "boolean") {
        await handleToggleGroupPaid(paidConfirm.group, paidConfirm.paid)
      } else if (paidConfirm.row != null && typeof paidConfirm.paid === "boolean") {
        await handleTogglePaid(paidConfirm.row, paidConfirm.paid)
      }
    } finally {
      setPaidConfirm({ row: null, group: null, paid: null })
    }
  }

  const openSubmitterDetailsPdf = useCallback(async group => {
    if (!baseUrl) return
    if (group.submitterId == null) {
      toast.error("Cannot open PDF: missing submitter id.")
      return
    }
    const q = { user_id: group.submitterId }
    const sliceIds = (group.items || []).map(r => r.id).filter(id => id != null)
    if (sliceIds.length) {
      q.line_item_ids = sliceIds.join(",")
    } else {
      if (filterDateFrom) q.occurred_on_from = filterDateFrom
      if (filterDateTo) q.occurred_on_to = filterDateTo
      if (filterPaid && filterPaid !== "all") q.paid_filter = filterPaid
      const accountsOnlyView =
        roleCheck(systemRole, currentRole, ACCOUNTS) && !isSystemAdmin(systemRole)
      if (!accountsOnlyView && filterStatus && filterStatus !== "all") {
        q.status_filter = filterStatus
      }
    }
    setPdfDetailsLoadingGroupKey(group.groupKey)
    try {
      const res = await openDashboardSubmitterReimbursementPdf(baseUrl, q)
      if (res.error) toast.error(res.error)
    } finally {
      setPdfDetailsLoadingGroupKey(null)
    }
  }, [baseUrl, filterDateFrom, filterDateTo, filterPaid, filterStatus, currentRole, systemRole])

  const clearFilters = () => {
    setFilterUserId("")
    setFilterDateFrom("")
    setFilterDateTo("")
    setFilterPaid("all")
    setFilterStatus("all")
  }

  const toggleHistory = () => {
    const next = !showHistory
    setShowHistory(next)
    if (next) {
      loadAuditRows()
    }
  }

  const admin = isSystemAdmin(systemRole)
  const accountsTeam = isAccountsTeam(currentRole)
  const canMarkPaid = accountsTeam
  const hideAccountsOnlyColumns = accountsTeam && !admin

  const consolidatedBySubmitter = useMemo(() => (
    hideAccountsOnlyColumns ? aggregateReimbursementsBySubmitter(rows) : []
  ), [hideAccountsOnlyColumns, rows])

  const dashboardRowsByStatus = useMemo(() => (
    hideAccountsOnlyColumns ? [] : groupDashboardRowsByStatus(rows)
  ), [hideAccountsOnlyColumns, rows])

  const hasActiveFilters = Boolean(
    filterUserId
    || filterDateFrom
    || filterDateTo
    || (filterPaid && filterPaid !== "all")
    || (!hideAccountsOnlyColumns && filterStatus && filterStatus !== "all")
  )

  const amountTotal = useMemo(() => sumReimbursementAmounts(rows), [rows])

  if (!canAccessDashboard(systemRole, currentRole)) {
    return null
  }

  return (
    <Container fluid>
      <div className="mb-3">
        <Link to="/settings">← Settings</Link>
      </div>
      <h1 className="mb-4">Reimbursements</h1>

      <div className="mb-3 d-flex flex-wrap align-items-center">
        <Button
          type="button"
          variant="link"
          className="p-0"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}
          aria-controls="reimbursements-dashboard-filters">
          {filtersOpen ? "- Hide filters" : "+ Show filters"}
        </Button>
        {hasActiveFilters && !filtersOpen && (
          <span className="text-muted small ml-2">Filters active</span>
        )}
      </div>

      <Collapse in={filtersOpen}>
        <div id="reimbursements-dashboard-filters" className="mb-4">
          <Form>
            <Row>
              <Col md={6} lg className="mb-3">
                <Form.Group controlId="reimb-filter-surveyor" className="mb-0">
                  <Form.Label>Surveyor</Form.Label>
                  <Form.Control
                    as="select"
                    value={filterUserId}
                    onChange={e => setFilterUserId(e.target.value)}>
                    <option value="">All surveyors</option>
                    {submitterOptions.map(s => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6} lg className="mb-3">
                <Form.Group controlId="reimb-filter-date-from" className="mb-0">
                  <Form.Label>Date from</Form.Label>
                  <Form.Control
                    type="date"
                    value={filterDateFrom}
                    onChange={e => setFilterDateFrom(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg className="mb-3">
                <Form.Group controlId="reimb-filter-date-to" className="mb-0">
                  <Form.Label>Date to</Form.Label>
                  <Form.Control
                    type="date"
                    value={filterDateTo}
                    onChange={e => setFilterDateTo(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg className="mb-3">
                <Form.Group controlId="reimb-filter-paid" className="mb-0">
                  <Form.Label>Paid</Form.Label>
                  <Form.Control
                    as="select"
                    value={filterPaid}
                    onChange={e => setFilterPaid(e.target.value)}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              {!hideAccountsOnlyColumns && (
                <Col md={6} lg className="mb-3">
                  <Form.Group controlId="reimb-filter-status" className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={filterStatus}
                      onChange={e => setFilterStatus(e.target.value)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                      <option value="closed">Closed</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Button type="button" variant="outline-secondary" size="sm" onClick={clearFilters} disabled={!hasActiveFilters}>
              Clear filters
            </Button>
          </Form>
        </div>
      </Collapse>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          {(hideAccountsOnlyColumns ? consolidatedBySubmitter.length === 0 : rows.length === 0) ? (
            <p className="text-muted">
              {hasActiveFilters ? "No reimbursements match these filters." : "No reimbursements yet."}
            </p>
          ) : (
            <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Status</th>
              <th>Date</th>
              <th>Name</th>
              <th>Email</th>
              {hideAccountsOnlyColumns ? (
                <th style={{ minWidth: "240px" }}>Actions</th>
              ) : (
                <>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Receipt</th>
                  <th>Decline reason</th>
                  <th>Paid</th>
                  <th style={{ minWidth: "220px" }}>Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {hideAccountsOnlyColumns ? (
              consolidatedBySubmitter.map(group => (
                <tr key={group.groupKey}>
                  <td>
                    <ConsolidatedAccountsStatusBadge group={group} />
                    <div className="text-muted small mt-1">{group.itemCount} line item{group.itemCount === 1 ? "" : "s"}</div>
                  </td>
                  <td>{group.dateLabel}</td>
                  <td>{group.submitterName || "—"}</td>
                  <td>{group.submitterEmail || "—"}</td>
                  <td>
                    <div className="d-flex flex-row flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
                      <ConsolidatedPaidCell
                        group={group}
                        canMarkPaid={canMarkPaid}
                        markingGroupKey={markingPaidGroupKey}
                        onRequestMarkAll={(grp, paidNext) =>
                          setPaidConfirm({ row: null, group: grp, paid: paidNext })
                        }
                      />
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 100 }}
                        overlay={
                          <Tooltip id={`reimb-pdf-details-${encodeURIComponent(group.groupKey).replace(/%/g, "")}`}>
                            View reimbursement details in pdf
                          </Tooltip>
                        }>
                        <span className="d-inline-block">
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            disabled={pdfDetailsLoadingGroupKey === group.groupKey}
                            onClick={() => openSubmitterDetailsPdf(group)}>
                            {pdfDetailsLoadingGroupKey === group.groupKey ? "Opening PDF…" : "View details (PDF)"}
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
            dashboardRowsByStatus.map(section => (
              <React.Fragment key={`status-section-${section.status}`}>
                <tr className="table-light">
                  <td colSpan={10}>
                    <strong>{formatStatusSectionTitle(section.status)}</strong>
                    <span className="text-muted ml-2">
                      ({section.rows.length}
                      {" "}
                      line item
                      {section.rows.length === 1 ? "" : "s"}
                      )
                    </span>
                  </td>
                </tr>
                {section.rows.map(row => {
              const pending = row.status === "pending"
              return (
                <tr key={row.id}>
                  <td>
                    <Badge variant={statusBadgeVariant(row.status)}>
                      {row.status}
                    </Badge>
                  </td>
                  <td>{row.occurredOn}</td>
                  <td>{row.submitterName || "—"}</td>
                  <td>{row.submitterEmail || "—"}</td>
                  <td>{row.description}</td>
                  <td>${row.amount}</td>
                  <td>
                    {row.receiptUrl ? (
                      <a href={row.receiptUrl} target="_blank" rel="noopener noreferrer">
                        View receipt
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  {!hideAccountsOnlyColumns && (
                    <td>{row.status === "declined" ? (row.declinedReason || "—") : "—"}</td>
                  )}
                  <td>
                    {(row.status === "approved" || row.status === "closed") ? (
                      canMarkPaid ? (
                        <Form.Check
                          id={`paid-${row.id}`}
                          type="checkbox"
                          className="mb-0"
                          checked={row.paid}
                          disabled={markingPaidRowId === row.id}
                          onChange={e => handlePaidCheckboxChange(row, e.target.checked)}
                          label={row.paid ? "Paid" : "Unpaid"}
                        />
                      ) : (
                        <Badge variant={row.paid ? "success" : "secondary"}>
                          {row.paid ? "Paid" : "Unpaid"}
                        </Badge>
                      )
                    ) : (
                      "—"
                    )}
                  </td>
                  {!hideAccountsOnlyColumns && (
                    <td>
                      <div className="d-flex flex-wrap">
                        {pending && (
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            className="mr-1 mb-1"
                            onClick={() => openEdit(row)}>
                            Edit
                          </Button>
                        )}
                        {admin && pending && (
                          <>
                            <Button
                              type="button"
                              variant="success"
                              size="sm"
                              className="mr-1 mb-1"
                              disabled={approvingId === row.id}
                              onClick={() => handleApprove(row)}>
                              {approvingId === row.id ? "…" : "Approve"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              className="mb-1"
                              onClick={() => openDecline(row)}>
                              Decline
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
              </React.Fragment>
            ))
            )}
          </tbody>
        </Table>
          )}
          <p className="mt-2 mb-3 font-weight-bold" aria-live="polite">
            Total: ${formatReimbursementTotal(amountTotal)}
            {hasActiveFilters ? " (filtered)" : ""}
          </p>
          {admin && (
            <Button
              type="button"
              variant="link"
              className="p-0 mb-3"
              onClick={toggleHistory}>
              {showHistory ? "Hide history" : "View history"}
            </Button>
          )}

          {admin && showHistory && (
            <div className="mb-4">
              <h2 className="h5 mb-3">Reimbursement Audit Trail</h2>
              {historyLoading ? (
                <p>Loading history…</p>
              ) : auditRows.length === 0 ? (
                <p className="text-muted">No audit history found.</p>
              ) : (
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Reimbursement ID</th>
                      <th>Event</th>
                      <th>Changed by</th>
                      <th>Changed fields</th>
                      <th>Change summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.map(audit => (
                      <tr key={audit.id}>
                        <td>{audit.changedAt || "—"}</td>
                        <td>{audit.reimbursementLineItemId || "—"}</td>
                        <td>{audit.event || "—"}</td>
                        <td>{audit.changedByName || "System"}</td>
                        <td>{Array.isArray(audit.changedFields) ? audit.changedFields.join(", ") : "—"}</td>
                        <td>{audit.changeSummary || audit.comment || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </>
      )}

      <Modal show={showEditModal} onHide={closeEdit} size="lg" centered backdrop="static">
        <Form onSubmit={handleSaveEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit reimbursement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={4} className="mb-3">
                <Form.Group controlId="edit-occurred-on">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={editForm.occurredOn}
                    onChange={e => setEditForm(f => ({ ...f, occurredOn: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group controlId="edit-amount">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={editForm.amount}
                    onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group controlId="edit-receipt">
                  <Form.Label>Receipt</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*,.pdf"
                    onChange={e => {
                      const file = e.target.files?.[0] || null
                      setEditForm(f => ({ ...f, receiptFile: file }))
                    }}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to keep the current receipt.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="edit-description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                required
                value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={closeEdit} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeclineModal} onHide={closeDecline} centered backdrop="static">
        <Form onSubmit={submitDecline}>
          <Modal.Header closeButton>
            <Modal.Title>Decline reimbursement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="decline-reason">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                required
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                placeholder="Explain why this reimbursement is declined."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={closeDecline} disabled={declining}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={declining}>
              {declining ? "Saving…" : "Decline"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {(paidConfirm.row || paidConfirm.group) && typeof paidConfirm.paid === "boolean" && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={paidConfirm.paid ? "Yes, mark as paid" : "Yes, mark as unpaid"}
          cancelBtnText="Cancel"
          title={
            paidConfirm.group
              ? paidConfirm.paid
                ? "Mark reimbursements as paid?"
                : "Mark reimbursements as unpaid?"
              : paidConfirm.paid
                ? "Mark reimbursement as paid?"
                : "Mark reimbursement as unpaid?"
          }
          onConfirm={confirmTogglePaid}
          onCancel={() => setPaidConfirm({ row: null, group: null, paid: null })}>
          {paidConfirm.group
            ? paidConfirm.paid
              ? `This will mark ${paidConfirm.group.unpaidApprovedIds.length} approved reimbursement line item(s) for ${paidConfirm.group.submitterName || "this submitter"} as paid.`
              : `This will mark ${paidConfirm.group.paidApprovedIds.length} reimbursement line item(s) for ${paidConfirm.group.submitterName || "this submitter"} as unpaid.`
            : paidConfirm.paid
              ? `This will mark reimbursement #${paidConfirm.row.id} as paid.`
              : `This will mark reimbursement #${paidConfirm.row.id} as unpaid.`}
        </SweetAlert>
      )}
    </Container>
  )
}
